import { useEffect, useState, type ReactNode } from "react";
import { HIDDEN_UPDATE, type UpdateBarState } from "../apkUpdate";
import {
  getApkSession,
  installDownloadedApk,
  refreshApkOffer,
  startApkDownload,
  subscribeApkSession,
} from "../apkSession";
import { COMPANION_PRODUCT } from "../companion";
import { bootNativeShell } from "../nativeShell";
import { useInstallHint } from "../useInstallHint";
import { APP_VERSION } from "../version";
import { AboutSheet } from "../views/AboutSheet";
import { InstallHint } from "../views/InstallHint";
import { UpdateBar } from "../views/UpdateBar";
import type { EditTab } from "./editTabs";
import { PilotBar, type PilotScreen } from "./PilotBar";
import type { CompanionTab, FlightTab } from "./tabs";
import { airlineLogoSrc, type ThemeId } from "@northstar/dispatch-core";

export function CompanionShell({
  tab,
  onTab,
  editTab,
  onEditTab,
  companyName,
  companyLogo = "",
  syncLabel,
  theme,
  onTheme,
  sheetOpen,
  onSheet,
  onSignOut,
  onSystemBack,
  renderEdit,
  updatePreview,
  flightOpen = false,
  flightTab = "brief",
  onFlightTab,
  screen = "home",
  onScreen,
  aboutTick = 0,
  menuOpen = false,
  onMenu,
  menuUser = "",
  menuBase = "",
  drafts = [],
  onOpenDraft,
  syncDown = false,
  children,
  locked = false,
}: {
  tab: CompanionTab;
  onTab: (next: CompanionTab) => void;
  editTab: EditTab;
  onEditTab: (next: EditTab) => void;
  companyName: string;
  companyLogo?: string;
  syncLabel: string;
  theme: ThemeId;
  onTheme: (next: ThemeId) => void;
  sheetOpen: boolean;
  onSheet: (open: boolean) => void;
  onSignOut?: () => void;
  onSystemBack?: () => void;
  renderEdit?: (tab: EditTab) => ReactNode;
  updatePreview?: UpdateBarState | null;
  onRefresh?: () => void;
  refreshing?: boolean;
  flightOpen?: boolean;
  flightTab?: FlightTab;
  onFlightTab?: (next: FlightTab) => void;
  screen?: PilotScreen;
  onScreen?: (next: PilotScreen) => void;
  /** Increment to open About from the More screen. */
  aboutTick?: number;
  menuOpen?: boolean;
  onMenu?: (open: boolean) => void;
  menuUser?: string;
  menuBase?: string;
  drafts?: { tripId: string; label: string }[];
  onOpenDraft?: (tripId: string) => void;
  syncDown?: boolean;
  children: ReactNode;
  locked?: boolean;
}) {
  const install = useInstallHint();
  const themeLabel = theme === "light" ? "Night" : "Day";
  const [aboutOpen, setAboutOpen] = useState(false);
  useEffect(() => {
    if (aboutTick > 0) setAboutOpen(true);
  }, [aboutTick]);
  const [aboutNote, setAboutNote] = useState("");
  const [offline, setOffline] = useState(false);
  const [update, setUpdate] = useState<UpdateBarState>(HIDDEN_UPDATE);
  const shownUpdate = updatePreview ?? update;

  useEffect(() => {
    setUpdate(getApkSession());
    return subscribeApkSession(() => setUpdate(getApkSession()));
  }, []);

  useEffect(() => {
    const storage = typeof localStorage === "undefined" ? null : localStorage;
    const run = (manual: boolean) => {
      void refreshApkOffer({ manual, storage })
        .then((message) => {
          if (manual || message) setAboutNote(message);
        })
        .catch(() => {
          if (manual) setAboutNote("The update check failed.");
        });
    };
    if (!updatePreview) run(false);
    const onVisible = () => {
      if (document.visibilityState === "visible") run(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onVisible);
    let resumeHandle: { remove: () => Promise<void> } | undefined;
    let resumeCancelled = false;
    if (!updatePreview) {
      void import("@capacitor/app")
        .then(({ App }) => App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) run(false);
        }))
        .then((handle) => {
          if (resumeCancelled) {
            void handle.remove();
            return;
          }
          resumeHandle = handle;
        })
        .catch(() => undefined);
    }
    const onOffline = () => setOffline(true);
    const onOnline = () => setOffline(false);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    setOffline(typeof navigator !== "undefined" && navigator.onLine === false);
    let removeNative: () => void = () => undefined;
    void bootNativeShell(() => {
      if (aboutOpen) {
        setAboutOpen(false);
        return;
      }
      if (menuOpen) {
        onMenu?.(false);
        return;
      }
      onSystemBack?.();
    }).then((dispose) => {
      removeNative = dispose;
    });
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      resumeCancelled = true;
      void resumeHandle?.remove();
      removeNative();
    };
  }, [aboutOpen, menuOpen, onMenu, onSystemBack, updatePreview]);
  const classes = ["companion", "is-pinned", locked ? "is-locked" : ""].filter(Boolean).join(" ");

  return (
    <div
      className={classes}
      data-testid="companion-shell"
      data-pinned="true"
      data-map-pin="false"
      data-locked={locked ? "true" : "false"}
      data-theme-mode={theme}
      onContextMenu={(event) => event.preventDefault()}
    >
      <header className="companion-header">
        <div className="companion-header-row">
          <div className="header-id">
            <p className="kicker header-brand">
              <svg className="ns-mark" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 1C13.21 9.36 14.64 10.79 23 12C14.64 13.21 13.21 14.64 12 23C10.79 14.64 9.36 13.21 1 12C9.36 10.79 10.79 9.36 12 1Z"
                />
              </svg>
              <span>
                {COMPANION_PRODUCT} · v{APP_VERSION}
              </span>
            </p>
            <p className="header-company">
              {airlineLogoSrc(companyLogo, undefined, { onDark: true }) ? (
                <img
                  className="header-company-logo"
                  src={airlineLogoSrc(companyLogo, undefined, { onDark: true }) ?? ""}
                  alt=""
                  data-testid="header-company-logo"
                />
              ) : null}
              {companyName}
            </p>
            <p className="header-sync mono">{syncLabel}</p>
          </div>
          {locked ? (
            <div className="header-actions">
              <button type="button" className="efb-text-btn" data-testid="about-open" onClick={() => setAboutOpen(true)}>
                About
              </button>
              <button
                type="button"
                className="efb-theme"
                data-testid="theme-toggle"
                onClick={() => onTheme(theme === "light" ? "dark" : "light")}
              >
                {themeLabel}
              </button>
            </div>
          ) : null}
        </div>
      </header>
      {locked ? null : (
        <nav className="efb-topbar" aria-label="Northstar Dispatch Mobile" data-testid="efb-topbar">
          <div className="efb-brand">
            <svg className="ns-mark" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 1C13.21 9.36 14.64 10.79 23 12C14.64 13.21 13.21 14.64 12 23C10.79 14.64 9.36 13.21 1 12C9.36 10.79 10.79 9.36 12 1Z"
              />
            </svg>
            <span>Northstar</span>
          </div>
          <div className="efb-modules">
            <span>{flightOpen ? "Flight" : "Today"}</span>
          </div>
          <div className="efb-top-meta">
            <span className="mono">{syncLabel}</span>
          </div>
        </nav>
      )}
      <InstallHint mode={install.mode} onInstall={install.onInstall} onDismiss={install.onDismiss} />
      {offline || syncDown ? (
        <p className="offline-banner" data-testid="offline-banner" role="status">
          {offline ? "Offline. Showing the last board stored on this phone." : "The board did not refresh. Showing the last copy on this phone."}
        </p>
      ) : null}
      <div className="efb-body">
        <main className="companion-main">{children}</main>
      </div>
      <UpdateBar state={shownUpdate} onDownload={() => void startApkDownload()} onInstall={() => void installDownloadedApk()} />
      {locked || !onScreen ? null : <PilotBar screen={flightOpen ? flightTab : screen} onScreen={onScreen} />}
      <AboutSheet
        open={aboutOpen}
        note={aboutNote}
        notes={shownUpdate.notes}
        onClose={() => setAboutOpen(false)}
        onCheck={() => {
          const storage = typeof localStorage === "undefined" ? null : localStorage;
          void refreshApkOffer({ manual: true, storage }).then(setAboutNote);
        }}
      />
      {menuOpen ? (
        <div className="efb-scrim" data-testid="side-menu" onClick={() => onMenu?.(false)}>
          <aside className="side-menu" onClick={(event) => event.stopPropagation()} aria-label="Menu">
            <p className="kicker">Account</p>
            <h2>{menuUser || "Pilot"}</h2>
            <p>{companyName}</p>
            <p className="muted">{menuBase ? `Base ${menuBase}` : "No base on this board"}</p>
            <h2>Log drafts</h2>
            {drafts.length ? (
              <ul className="pilot-list">
                {drafts.map((row) => (
                  <li key={row.tripId}>
                    <button type="button" className="log-time" onClick={() => onOpenDraft?.(row.tripId)}>
                      {row.label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No saved log drafts.</p>
            )}
            {onSignOut ? (
              <button type="button" className="btn ghost" data-testid="menu-sign-out" onClick={onSignOut}>
                Log out
              </button>
            ) : null}
          </aside>
        </div>
      ) : null}
    </div>
  );
}
